import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { BusinessPartnerContactPerson, CustomerIdentificationQuery } from '@oe-backend/types';
import { OAuthService } from 'angular-oauth2-oidc';
import { OAuthEvent } from 'angular-oauth2-oidc/events';
import { BehaviorSubject, filter, firstValueFrom, map, Observable } from 'rxjs';
import { Helper } from '../../helper/helper';
import { LOCAL_USER_DATA_STORAGE } from '../../shared/injection-token/local-user-data.token';
import { LocalUserData } from '../../types/interfaces/local-user-data.interface';
import { UserData } from '../../types/interfaces/user-data.interface';

@Injectable()
export class UserDataService {
	private readonly storageService = inject(LOCAL_USER_DATA_STORAGE);
	private readonly $storageValue = this.storageService.value;

	private zeissIdToken$: BehaviorSubject<UserData | null> = new BehaviorSubject<UserData | null>(
		null,
	);

	constructor(
		private readonly oauthService: OAuthService,
		private readonly transLocoService: TranslocoService,
	) {
		this.parseAndSaveZeissIdUserData();
		this.setLanguageBasedOnUserData();
		this.reloadUserDataAfterTokenChanged();
	}

	reloadUserDataAfterTokenChanged(): void {
		this.oauthService.events.subscribe((eventObject: OAuthEvent) => {
			if (eventObject.type === 'token_received') {
				this.parseAndSaveZeissIdUserData();
				this.setLanguageBasedOnUserData();
			}
		});
	}

	getAccountId(): Observable<number | undefined> {
		return this.zeissIdToken$.asObservable().pipe(
			filter((token) => token != null),
			map((token) => token?.ZeissIdBase.accountId),
		);
	}

	getFirstName(): string | undefined {
		return this.zeissIdToken$.value?.ZeissIdBase.firstName;
	}

	doesUserHaveRole(role: string): boolean {
		return this.zeissIdToken$.value?.roles?.indexOf(role) !== -1 || false;
	}

	getLastName(): string | undefined {
		return this.zeissIdToken$.value?.ZeissIdBase.lastName;
	}

	getEmail(): string | undefined {
		return this.zeissIdToken$.value?.ZeissIdBase.eMail;
	}

	getInitials(): string | undefined {
		return this.zeissIdToken$.value?.ZeissIdBase.firstName[0].concat(
			this.zeissIdToken$.value?.ZeissIdBase.lastName[0],
		);
	}

	getUserLanguage(): string | undefined {
		return this.zeissIdToken$.value?.ZeissIdBase.language
			? this.zeissIdToken$.value?.ZeissIdBase.language
			: undefined;
	}

	getUserCountry(): string | undefined {
		return this.zeissIdToken$.value?.ZeissIdContact.country;
	}

	async getSelectedSoldToStore(): Promise<CustomerIdentificationQuery | undefined> {
		return (await this.getLocalUserData())?.selectedSoldToStore;
	}

	async setSelectedSoldToStore(selectedSoldToStore?: CustomerIdentificationQuery) {
		await this.patchLocalStorageData({ selectedSoldToStore });
	}

	async oldItemToastDisplayedToday(): Promise<boolean> {
		const oldToastDisplayDateString = (await this.getLocalUserData())?.oldItemToastDisplayedDate;
		if (!oldToastDisplayDateString) return false;

		const oldToastDisplayDate = new Date(oldToastDisplayDateString);
		const currentDate = new Date();
		return (
			oldToastDisplayDate.getDate() === currentDate.getDate() &&
			oldToastDisplayDate.getMonth() === currentDate.getMonth() &&
			oldToastDisplayDate.getFullYear() === currentDate.getFullYear()
		);
	}

	async setOldItemToastDisplayed() {
		await this.patchLocalStorageData({ oldItemToastDisplayedDate: new Date().toISOString() });
	}

	async getDefaultContactPerson(
		shipToCustomerNumber: number,
	): Promise<BusinessPartnerContactPerson | null> {
		const defaultShipToContacts = (await this.getLocalUserData())?.defaultShipToContacts;
		if (!defaultShipToContacts) return null;
		return defaultShipToContacts[shipToCustomerNumber] || null;
	}

	async setDefaultContactPerson(
		shipToCustomerNumber: number,
		defaultContactPerson: BusinessPartnerContactPerson,
	) {
		const defaultShipToContacts = (await this.getLocalUserData())?.defaultShipToContacts;
		this.patchLocalStorageData({
			defaultShipToContacts: {
				...defaultShipToContacts,
				[shipToCustomerNumber]: defaultContactPerson,
			},
		});
	}

	/**
	 * Checks if a specified key is present in the storage for a given property of the local user data.
	 *
	 * @template T - The type of the local user data.
	 * @template K - The type of the property.
	 * @param key - The key of the local user data to check.
	 * @param value - The value to check within the specified property. Optional.
	 * @returns A promise that resolves to a boolean indicating whether the key is present in the storage.
	 */
	async isKeyInStorage<T extends keyof LocalUserData, K extends keyof T>(
		key: keyof LocalUserData,
		value?: T[K],
	): Promise<boolean> {
		const userData = await this.getLocalUserData();
		// really unhappy with the cast of userData[property] as any but I dont know how to do it better cause userData[property][key] is not working and I dont know why.
		return !!(userData && userData[key] && (userData[key] as any)[value]);
	}

	/**
	 * Sets a property in the local storage for the user data.
	 *
	 * @template T - The type of the local user data.
	 * @template K - The type of the key for the value.
	 * @param {keyof LocalUserData} key - The key to set in the user data.
	 * @param {T[K]} value - The value for the key.
	 * @returns {Promise<void>} - A promise that resolves when the property is set in the local storage.
	 */
	async setPropertyInStorage<T extends keyof LocalUserData, K extends keyof T>(
		key: keyof LocalUserData,
		value: T[K],
	): Promise<void> {
		const userData = await this.getLocalUserData();
		const dataToUpdate = userData ? userData[key] : {};

		this.patchLocalStorageData({
			[key]: {
				...(typeof dataToUpdate === 'object' ? dataToUpdate : { dataToUpdate }),
				[value as string]: true,
			},
		});
	}

	private async getLocalUserData(): Promise<LocalUserData | undefined> {
		const accountId = await firstValueFrom(this.getAccountId());
		const storageData = this.$storageValue();
		if (!storageData || !accountId) return;
		return storageData[accountId];
	}

	private async patchLocalStorageData(data: Partial<LocalUserData>) {
		const accountId = await firstValueFrom(this.getAccountId());
		if (!accountId) return;

		const storageData = this.$storageValue() || {};
		storageData[accountId] = {
			...storageData[accountId],
			...data,
		};

		this.storageService.setValue(storageData);
	}

	private parseAndSaveZeissIdUserData(): void {
		if (this.oauthService.getIdToken()) {
			const temporaryZeissIdToken = this.parseJwt(this.oauthService.getIdToken());
			temporaryZeissIdToken.ZeissIdBase = Helper.parseJson(temporaryZeissIdToken.ZeissIdBase);
			temporaryZeissIdToken.ZeissIdAgreements = Helper.parseJson(
				temporaryZeissIdToken.ZeissIdAgreements,
			);
			temporaryZeissIdToken.ZeissIdApplication = Helper.parseJson(
				temporaryZeissIdToken.ZeissIdApplication,
			);
			temporaryZeissIdToken.ZeissIdContact = Helper.parseJson(temporaryZeissIdToken.ZeissIdContact);
			this.zeissIdToken$.next(temporaryZeissIdToken);
		}
	}

	private parseJwt(token: string) {
		const base64Url = token.split('.')[1];
		const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
		const jsonPayload = decodeURIComponent(
			window
				.atob(base64)
				.split('')
				.map(function (c) {
					return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
				})
				.join(''),
		);
		return Helper.parseJson(jsonPayload);
	}

	private setLanguageBasedOnUserData(): void {
		if (this.zeissIdToken$.value) {
			this.transLocoService.setActiveLang(
				this.zeissIdToken$.value.ZeissIdBase.language.toLowerCase(),
			);
		} else {
			console.log('Cannot set language because ZeissIDToken is not loaded');
		}
	}
}
